"use client";

import * as React from "react";
import { Switch as BaseSwitch } from "@base-ui/react/switch";
import { mergeAriaIds } from "../../utils/aria";
import styles from "./Toggle.module.scss";

/**
 * Binary on/off switch for settings and preferences.
 * @see https://usefragments.com/components/switch
 */
export interface SwitchProps {
  /** Whether the switch is on */
  checked?: boolean;
  /** Default checked state (uncontrolled) */
  defaultChecked?: boolean;
  /** Called when the switch is toggled */
  onCheckedChange?: (checked: boolean) => void;
  /** Alias for onCheckedChange */
  onChange?: (checked: boolean) => void;
  label?: string;
  /** Helper text shown below the label */
  helperText?: string;
  /** @deprecated Use helperText instead. */
  description?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  size?: "sm" | "md" | "lg";
  className?: string;
  name?: string;
  form?: string;
  value?: string;
  uncheckedValue?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  id?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
}

export type ToggleProps = SwitchProps;

const SwitchRoot = React.forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  {
    checked,
    defaultChecked,
    onChange,
    onCheckedChange,
    label,
    helperText,
    description,
    disabled = false,
    readOnly = false,
    required = false,
    size = "md",
    className,
    name,
    form,
    value,
    uncheckedValue,
    inputRef,
    id,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedBy,
  },
  ref
) {
  const resolvedHelperText = helperText ?? description;
  const generatedId = React.useId();
  const resolvedId = id ?? `switch-${generatedId}`;
  const helperId = resolvedHelperText ? `${resolvedId}-helper` : undefined;
  const trackClasses = [
    styles.track,
    size === "sm" ? styles.trackSm : size === "lg" ? styles.trackLg : styles.trackMd,
  ].join(" ");

  const thumbClasses = styles.thumb;

  const labelClasses = [
    styles.label,
    size === "sm" && styles.labelSm,
    size === "lg" && styles.labelLg,
  ]
    .filter(Boolean)
    .join(" ");

  const helperClasses = [styles.helper, size === "sm" && styles.helperSm].filter(Boolean).join(" ");

  const rootClasses = [styles.root, resolvedHelperText && styles.rootWithHelper, className]
    .filter(Boolean)
    .join(" ");

  return (
    <BaseSwitch.Root
      ref={ref}
      id={resolvedId}
      checked={checked}
      defaultChecked={defaultChecked}
      onCheckedChange={onCheckedChange ?? onChange}
      disabled={disabled}
      readOnly={readOnly}
      required={required}
      name={name}
      form={form}
      value={value}
      uncheckedValue={uncheckedValue}
      inputRef={inputRef}
      className={rootClasses}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={mergeAriaIds(ariaDescribedBy, helperId)}
    >
      <span className={trackClasses} aria-hidden="true">
        <BaseSwitch.Thumb className={thumbClasses} />
      </span>

      {(label || resolvedHelperText) && (
        <div className={styles.content}>
          {label && <span className={labelClasses}>{label}</span>}
          {resolvedHelperText && (
            <span id={helperId} className={helperClasses}>
              {resolvedHelperText}
            </span>
          )}
        </div>
      )}
    </BaseSwitch.Root>
  );
});

export const Switch = Object.assign(SwitchRoot, {
  Root: SwitchRoot,
});

/** @deprecated Use `Switch` instead. */
export const Toggle = Switch;
